import React, { useState, useEffect } from 'react';
import './App.css';
import ConfigurationList from './components/ConfigurationList';
import ConfigurationForm from './components/ConfigurationForm';
import Login from './components/Login';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function App() {
  const [user, setUser] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [serverTypes, setServerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token
      axios.get(`${API_BASE}/auth/me`)
        .then(res => {
          setUser(res.data.user);
          loadData();
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    // loadData will be called by useEffect when user changes
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setConfigurations([]);
    setServerTypes([]);
  };

  const loadData = async () => {
    if (!user) return;
    
    try {
      const [configsRes, serverTypesRes] = await Promise.all([
        axios.get(`${API_BASE}/configurations`),
        axios.get(`${API_BASE}/server-types`)
      ]);
      setConfigurations(configsRes.data);
      setServerTypes(serverTypesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);


  const handleSave = async (config) => {
    try {
      if (config.id) {
        await axios.put(`${API_BASE}/configurations/${config.id}`, config);
      } else {
        await axios.post(`${API_BASE}/configurations`, config);
      }
      await loadData();
      setShowForm(false);
      setSelectedConfig(null);
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Error saving configuration');
    }
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setShowForm(true);
  };

  const handleDestroyResources = async (id) => {
    const message = '⚠️ DANGER: This will destroy all resources in Hetzner!\n\n' +
                  'This includes:\n' +
                  '- The Hetzner server\n' +
                  '- All DNS records in Cloudflare\n' +
                  '- All data on the server\n\n' +
                  'The configuration will be KEPT in the UI.\n' +
                  'You can redeploy later with the same configuration.\n\n' +
                  'This action CANNOT be undone!\n\n' +
                  'Are you absolutely sure?';

    if (window.confirm(message)) {
      try {
        const response = await axios.post(`${API_BASE}/configurations/${id}/destroy`);
        alert('Resource destruction started. This may take a few minutes. The configuration will be kept.');
        await loadData();
      } catch (error) {
        console.error('Error destroying resources:', error);
        alert('Error destroying resources: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleDelete = async (id, destroyResources = false) => {
    const config = configurations.find(c => c.id === id);
    const isDeployed = config?.deploymentStatus === 'deployed' || config?.deploymentStatus === 'completed';
    
    let message = 'Are you sure you want to delete this configuration?';
    if (isDeployed && !destroyResources) {
      message += '\n\n⚠️ WARNING: This will only delete the configuration from the UI.\n' +
                 'The server and resources in Hetzner will NOT be deleted.\n\n' +
                 'To also delete the server and all resources, click "Delete & Destroy Resources" instead.';
    } else if (isDeployed && destroyResources) {
      message = '⚠️ DANGER: This will delete the configuration AND destroy all resources in Hetzner!\n\n' +
                'This includes:\n' +
                '- The Hetzner server\n' +
                '- All DNS records in Cloudflare\n' +
                '- All data on the server\n\n' +
                'This action CANNOT be undone!\n\n' +
                'Are you absolutely sure?';
    }

    if (window.confirm(message)) {
      try {
        const url = destroyResources 
          ? `${API_BASE}/configurations/${id}?destroyResources=true`
          : `${API_BASE}/configurations/${id}`;
        
        await axios.delete(url);
        await loadData();
        
        if (destroyResources) {
          alert('Resource destruction started. This may take a few minutes.');
        }
      } catch (error) {
        console.error('Error deleting configuration:', error);
        alert('Error deleting configuration: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleDeploy = async (id) => {
    try {
      const response = await axios.post(`${API_BASE}/configurations/${id}/deploy`);
      // Status will be updated via polling in ConfigurationList
    } catch (error) {
      console.error('Error deploying:', error);
      alert('Error starting deployment: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdate = async (id) => {
    await axios.post(`${API_BASE}/configurations/${id}/update`);
  };

  const handleBuild = async (id) => {
    if (!window.confirm('Run builder to install dependencies?\n\nThis will:\n- Install Composer dependencies\n- Build Vue.js applications\n- Restart services\n\nBuilder logs will be available after starting.')) {
      return;
    }
    try {
      const response = await axios.post(`${API_BASE}/configurations/${id}/build`);
      // Show builder logs modal instead of alert
      // The ConfigurationList component will handle showing logs
      console.log('Builder started:', response.data);
    } catch (error) {
      console.error('Error running builder:', error);
      alert('Error starting builder: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div>
          <h1>Exolog-Hetzner Deployment Manager</h1>
          <span className="user-info">Logged in as: {user.username}</span>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setSelectedConfig(null);
              setShowForm(true);
            }}
          >
            + New Configuration
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </header>

      <main className="App-main">
        {showForm ? (
          <ConfigurationForm
            config={selectedConfig}
            serverTypes={serverTypes}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setSelectedConfig(null);
            }}
          />
        ) : (
          <ConfigurationList
            configurations={configurations}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDestroyResources={handleDestroyResources}
            onDeploy={handleDeploy}
            onUpdate={handleUpdate}
            onBuild={handleBuild}
          />
        )}
      </main>
    </div>
  );
}

export default App;
